import {
  FieldErrorProps,
  FieldError as RAFieldError,
  ValidationResult,
} from "react-aria-components";
import { usePlosTranslations } from "../../../contexts/PlosTranslationsContext";
import Error from "../../../img/icons/error.svg?react";
import { isKeyInType } from "../../../utils/typeGuards";
import styles from "./FieldError.module.css";

type ValidationMessagesMap = Partial<
  Record<keyof ValidationResult["validationDetails"], string>
>;
export type ErrorMessageType =
  | string
  | ValidationMessagesMap
  | (({
      validationDetails,
    }: {
      validationDetails: ValidityState;
    }) => string | null);

interface IFieldError extends FieldErrorProps {
  errorMessage?: ErrorMessageType;
}

const getErrorMessage = ({
  defaultMessages,
  errorMessage,
  validationDetails,
}: {
  defaultMessages: { valueMissing: string; typeMismatch: string };
  errorMessage?: ErrorMessageType;
  validationDetails: ValidityState;
}) => {
  let message: string | null = null;
  if (typeof errorMessage === "function") {
    message = errorMessage({ validationDetails });
  } else if (typeof errorMessage === "string") {
    message = errorMessage;
  } else {
    const customMessages = {
      ...defaultMessages,
      ...errorMessage,
    };
    for (const key in validationDetails) {
      if (isKeyInType(validationDetails, key) && validationDetails[key]) {
        message =
          (isKeyInType(customMessages, key) && customMessages[key]) || null;
        break;
      }
    }
  }

  return message ? (
    <>
      <Error />
      {message}
    </>
  ) : null;
};

/** The `FieldError` component displays validation errors for form fields.
 * Validation can occur when the user unfocuses a field or submits a form.
 * #### How it works:
 * The component reads default validation messages from `PlosTranslationsContext`.
 * Wrap your app in `<PlosTranslationsProvider>` to supply translated messages.
 *
 * The optional `errorMessage` prop can be:
 * - A `string`: A static error message displayed when the field has an error.
 * - A `ValidationMessagesMap`: A map of validation keys to custom messages.
 * - A `function`: Receives `validationDetails` and returns a string or `null`.
 * */
const FieldError = ({ errorMessage, ...props }: IFieldError) => {
  const { fieldError: defaultMessages } = usePlosTranslations();
  return (
    <RAFieldError className={styles.fieldError} {...props}>
      {({ validationDetails }) => {
        return getErrorMessage({
          defaultMessages,
          errorMessage,
          validationDetails,
        });
      }}
    </RAFieldError>
  );
};

export default FieldError;
